import { ErrorMessage, useFormikContext, useField } from "formik";
import React, { useState } from "react";
import InputError from "../input-error/input-error";
import Input from "./input.component";

type FormikInputProps = {
  label?: string | false;
  showLabel?: boolean;
  name: string;
  id: string;
  type?: string;
  placeholder?: string;
  maxLength?: number;
  showCount?: boolean;
  isPassword?: boolean;
  disabled?: boolean;
  sanitized?: boolean;
  isPercentage?: boolean;
  min?: number;
  max?: number;
  autoComplete?: string | boolean;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  className?: string;
  showLink?: boolean;
  linkText?: string;
  onLinkClick?: () => void;
  // Legacy props (ignored, kept for backwards compatibility)
  as?: string;
  value?: unknown;
  onDataChanged?: unknown;
};

const FormikInput: React.FC<FormikInputProps> = ({
  label,
  showLabel = true,
  name,
  id,
  type = "text",
  placeholder,
  maxLength,
  showCount = false,
  isPassword = false,
  disabled = false,
  sanitized = false,
  isPercentage = false,
  min,
  max,
  autoComplete,
  onBlur,
  className,
  showLink,
  linkText,
  onLinkClick,
}) => {
  const { setFieldValue } = useFormikContext<any>();
  const [field, meta, helpers] = useField(name);
  const [errorMessage, setErrorMessage] = useState("");
  const hasFormikError = meta.touched && !!meta.error;

  const currentValue = field.value ?? "";

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (type === "number") {
      if (value === "") {
        setFieldValue(name, "");
        setErrorMessage("");
        return;
      }
      const numValue = Number(value);
      if (min !== undefined && numValue < min) return;
      if (max !== undefined && numValue > max) return;
      setFieldValue(name, value);
      setErrorMessage("");
      return;
    }

    if (typeof value === "string") {
      const processedValue = sanitized ? value.replace(/\s/g, "") : value;

      if (maxLength && processedValue.length > maxLength) {
        setErrorMessage(`Maximum character limit of ${maxLength} exceeded.`);
        return;
      }

      setFieldValue(name, sanitized ? processedValue.trim() : processedValue);
      setErrorMessage("");
    }
  };

  return (
    <>
      <Input
        label={label || undefined}
        showLabel={showLabel}
        id={id}
        name={name}
        type={isPassword ? "password" : type}
        placeholder={placeholder}
        value={currentValue}
        disabled={disabled}
        maxLength={maxLength}
        showCount={showCount}
        min={min}
        max={max}
        hasError={!!errorMessage || hasFormikError}
        errorMessage={errorMessage}
        suffix={isPercentage ? "%" : undefined}
        showLink={showLink}
        linkText={linkText}
        onLinkClick={onLinkClick}
        autoComplete={autoComplete === false ? "off" : autoComplete === true ? "on" : autoComplete}
        onChange={handleChange}
        onBlur={(e) => {
          helpers.setTouched(true);
          onBlur?.(e);
        }}
        className={className}
      />
      {!errorMessage && <ErrorMessage name={name} component={InputError} />}
    </>
  );
};

export default FormikInput;
